/**
 * Derivations behind the Graphs tab.
 *
 * Each one answers a question a distributor actually asks, and each was checked
 * against a real company before it was written — the ones that could not be
 * supported (item margin, collection behaviour, salesperson performance) are
 * absent rather than approximated.
 *
 * All pure, all from the snapshot already on the device.
 */
import type { Snapshot, Voucher } from "@tally/shared";

import { indexGroups, isReceivable, isSalesLedger, money, sum } from "./groups.js";
import type { GroupIndex } from "./groups.js";
import { postedVouchers } from "./reports.js";

const isSale = (v: Voucher): boolean => /sale|invoice/i.test(v.voucherType);
const isPurchase = (v: Voucher): boolean => /purchase/i.test(v.voucherType);

function salesContext(snapshot: Snapshot, index: GroupIndex) {
  const debtors = new Set(
    snapshot.ledgers.filter((l) => isReceivable(index, l)).map((l) => l.name),
  );
  const revenue = new Set(
    snapshot.ledgers.filter((l) => isSalesLedger(index, l)).map((l) => l.name),
  );

  const partyOf = (v: Voucher): string | null =>
    (v.partyLedger && debtors.has(v.partyLedger) ? v.partyLedger : null) ??
    v.ledgerEntries.find((e) => debtors.has(e.ledger))?.ledger ??
    null;

  // Revenue lines, not the debtor line: the debtor amount carries tax, and
  // every comparison built on tax-inclusive figures flatters whoever pays the
  // higher rate.
  const valueOf = (v: Voucher): number =>
    Math.abs(sum(v.ledgerEntries.filter((e) => revenue.has(e.ledger)).map((e) => e.amount)));

  return { partyOf, valueOf, debtors };
}

/**
 * Is there a sale in here at all?
 *
 * Nearly every chart below is built from sale vouchers, and a redacted copy can
 * arrive without one. Drawing an empty axis in that case reads as "we sold
 * nothing", which is a statement about the business rather than about the copy.
 */
export function hasSales(snapshot: Snapshot): boolean {
  return postedVouchers(snapshot).some(isSale);
}

export type Bar = { label: string; value: number };

/* ─────────────────────────────────────────────────────────────── 1 · sales ── */

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Which days of the week carry the trade. */
export function salesByWeekday(snapshot: Snapshot, index: GroupIndex = indexGroups(snapshot)): Bar[] {
  const { valueOf } = salesContext(snapshot, index);
  const totals = new Array(7).fill(0);

  for (const v of postedVouchers(snapshot)) {
    if (!isSale(v)) continue;
    // Parsed as UTC deliberately: a local-time parse shifts dates by a day for
    // anyone east of Greenwich, which silently moves Monday's sales to Sunday.
    const day = new Date(`${v.date}T00:00:00Z`).getUTCDay();
    totals[day] += valueOf(v);
  }

  // Monday first — a trading week, not a calendar one.
  return [1, 2, 3, 4, 5, 6, 0].map((d) => ({
    label: WEEKDAYS[d]!.slice(0, 3),
    value: money(totals[d]),
  }));
}

/** Where the customers are. Depends on `state` being set on ledgers. */
export function salesByState(
  snapshot: Snapshot,
  index: GroupIndex = indexGroups(snapshot),
): { bars: Bar[]; coverage: number } {
  const { partyOf, valueOf } = salesContext(snapshot, index);
  const stateOf = new Map(snapshot.ledgers.map((l) => [l.name, l.state?.trim() ?? ""]));

  const totals = new Map<string, number>();
  let known = 0;
  let all = 0;

  for (const v of postedVouchers(snapshot)) {
    if (!isSale(v)) continue;
    const party = partyOf(v);
    if (!party) continue;

    const value = valueOf(v);
    all += value;
    const state = stateOf.get(party);
    if (!state) continue;

    known += value;
    totals.set(state, money((totals.get(state) ?? 0) + value));
  }

  return {
    bars: [...totals.entries()].map(([label, value]) => ({ label, value })),
    // Shown on the chart: a regional split covering 60% of sales is a different
    // claim from one covering 99%, and the reader must be told which.
    coverage: all > 0 ? (known / all) * 100 : 0,
  };
}

export type NewVsReturning = { month: string; fresh: number; returning: number };

/** Is growth coming from new customers or from the ones already here? */
export function newVsReturning(
  snapshot: Snapshot,
  index: GroupIndex = indexGroups(snapshot),
): NewVsReturning[] {
  const { partyOf, valueOf } = salesContext(snapshot, index);
  const sales = postedVouchers(snapshot).filter(isSale).sort((a, b) => a.date.localeCompare(b.date));

  const firstMonth = new Map<string, string>();
  for (const v of sales) {
    const party = partyOf(v);
    if (party && !firstMonth.has(party)) firstMonth.set(party, v.date.slice(0, 7));
  }

  const months = new Map<string, { fresh: number; returning: number }>();
  for (const v of sales) {
    const party = partyOf(v);
    if (!party) continue;
    const month = v.date.slice(0, 7);
    const row = months.get(month) ?? { fresh: 0, returning: 0 };
    // "New" means first seen THIS month — not new to the world, new to us, in
    // the window we hold.
    if (firstMonth.get(party) === month) row.fresh = money(row.fresh + valueOf(v));
    else row.returning = money(row.returning + valueOf(v));
    months.set(month, row);
  }

  return [...months.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([month, row]) => ({ month, ...row }));
}

/** How many customers appeared for the first time, month by month. */
export function newCustomersByMonth(
  snapshot: Snapshot,
  index: GroupIndex = indexGroups(snapshot),
): Bar[] {
  const { partyOf } = salesContext(snapshot, index);
  const first = new Map<string, string>();

  for (const v of postedVouchers(snapshot).filter(isSale).sort((a, b) => a.date.localeCompare(b.date))) {
    const party = partyOf(v);
    if (party && !first.has(party)) first.set(party, v.date.slice(0, 7));
  }

  const counts = new Map<string, number>();
  for (const month of first.values()) counts.set(month, (counts.get(month) ?? 0) + 1);

  return [...counts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([label, value]) => ({ label: label.slice(2), value }));
}

/* ──────────────────────────────────────────────────────────── 2 · products ── */

export type ParetoPoint = { label: string; value: number; cumulativePercent: number };

export type Pareto = {
  points: ParetoPoint[];
  total: number;
  /** how many items make 80% of the money, and what share of the range that is */
  countTo80: number;
  shareTo80: number;
};

/**
 * The Pareto curve, and where 80% actually falls.
 *
 * Reported rather than assumed. The 80/20 rule is a rule of thumb, not a law —
 * on the first real company 33% of items made 80% of revenue, and a chart that
 * had asserted 20% would have been wrong about the very thing it existed to
 * show.
 */
export function paretoItems(snapshot: Snapshot): Pareto {
  const byItem = new Map<string, number>();

  for (const v of postedVouchers(snapshot)) {
    if (!isSale(v)) continue;
    for (const line of v.inventoryEntries) {
      byItem.set(line.stockItem, money((byItem.get(line.stockItem) ?? 0) + Math.abs(line.amount)));
    }
  }

  const sorted = [...byItem.entries()].sort((a, b) => b[1] - a[1]);
  const total = sum(sorted.map(([, v]) => v));

  let running = 0;
  const points = sorted.map(([label, value]) => {
    running += value;
    return { label, value, cumulativePercent: total > 0 ? (running / total) * 100 : 0 };
  });

  /*
   * How many items are needed to REACH 80% — the first one that crosses the
   * line, inclusive.
   *
   * Counting the items that sit at or BELOW 80% instead is off by one and
   * understates the concentration: four equal items are 25/50/75/100, and
   * three of them only get you to 75%. On a chart whose entire purpose is to
   * say "this few items carry the business", being one out is the whole answer.
   */
  const crossing = points.findIndex((p) => p.cumulativePercent >= 80);
  const countTo80 = crossing === -1 ? points.length : crossing + 1;

  return {
    points,
    total,
    countTo80,
    shareTo80: points.length > 0 ? (countTo80 / points.length) * 100 : 0,
  };
}

export type PriceSaleLine = {
  date: string;
  rate: number;
  qty: number;
  /** empty when the voucher names no customer we can resolve */
  customer: string;
  voucherId: string;
  voucherNumber: string;
};

export type PriceSpread = {
  item: string;
  low: number;
  high: number;
  median: number;
  sales: number;
  /** high ÷ low; 1 means one price for everyone */
  ratio: number;
  /**
   * True when at least one line went out for effectively nothing.
   *
   * NOT `rate === 0`. On the first real company the zero-rate lines were later
   * corrected in Tally to ₹0.01 — a token value that is still a giveaway and
   * slipped straight past an exact-zero test, silently removing the warning
   * from four items. The rule is therefore relative: a line under 1% of the
   * item's own median price is a giveaway whatever the currency.
   */
  nearlyFree: boolean;
  /**
   * Every sale of this item, cheapest first.
   *
   * The spread is only half the finding: "sold between ₹0 and ₹2,200" prompts
   * exactly one question, and it is "to whom". Carrying the lines means the
   * answer is one tap away rather than a hunt through the Sales Register.
   *
   * Cheapest first because the outliers are the point — the ₹0 line is what
   * needs explaining, and it should not be buried in the middle of a date sort.
   */
  lines: PriceSaleLine[];
};

/**
 * The same item, sold at different prices.
 *
 * Margin leaks here quietly and nobody reports it: a discount given once
 * becomes the price that customer expects forever. Only items with three or
 * more sales are included — two sales at two prices is an anecdote.
 */
export function priceSpread(
  snapshot: Snapshot,
  minSales = 3,
  index: GroupIndex = indexGroups(snapshot),
): PriceSpread[] {
  const { partyOf } = salesContext(snapshot, index);
  const rates = new Map<string, PriceSaleLine[]>();

  for (const v of postedVouchers(snapshot)) {
    if (!isSale(v)) continue;
    for (const line of v.inventoryEntries) {
      if (line.rate === undefined || line.rate === null) continue;
      const list = rates.get(line.stockItem) ?? [];
      list.push({
        date: v.date,
        rate: Math.abs(line.rate),
        qty: Math.abs(line.qty),
        customer: partyOf(v) ?? "",
        voucherId: v.id,
        voucherNumber: v.voucherNumber,
      });
      rates.set(line.stockItem, list);
    }
  }

  const rows: PriceSpread[] = [];
  for (const [item, lines] of rates) {
    if (lines.length < minSales) continue;
    const list = lines.map((l) => l.rate);
    const sorted = [...list].sort((a, b) => a - b);
    const low = sorted[0]!;
    const high = sorted[sorted.length - 1]!;
    if (high === low) continue;

    const mid = Math.floor(sorted.length / 2);
    const median =
      sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;

    rows.push({
      item,
      low,
      high,
      median: money(median),
      sales: list.length,
      lines: [...lines].sort((a, b) => a.rate - b.rate || a.date.localeCompare(b.date)),
      // Guard the division: a free line makes low zero, and Infinity would sort
      // to the top forever and crash the axis.
      ratio: low > 0 ? high / low : Number.POSITIVE_INFINITY,
      nearlyFree: low === 0 || (median > 0 && low < median * 0.01),
    });
  }

  return rows.sort((a, b) => {
    if (a.nearlyFree !== b.nearlyFree) return a.nearlyFree ? -1 : 1;
    return b.ratio - a.ratio;
  });
}

/* ─────────────────────────────────────────────────────────────── 3 · stock ── */

export const STOCK_AGE_BUCKETS = ["0-30", "31-60", "61-90", "90+ / never"] as const;
export type StockAgeBucket = (typeof STOCK_AGE_BUCKETS)[number];

export type AgedStockItem = {
  item: string;
  qty: number;
  unit: string;
  value: number;
  /** null when it has never sold inside the window */
  lastSold: string | null;
  /** null alongside lastSold — "never" is not a number of days */
  daysSince: number | null;
};

export type StockAgeBucketRow = {
  label: StockAgeBucket;
  value: number;
  items: number;
  /**
   * The items themselves, so a bucket can be opened.
   *
   * Carried on the bucket rather than fetched by a second pass: the caller
   * already has the answer, and a "which items are these?" that re-derives from
   * the snapshot could disagree with the bar above it after any rule change.
   */
  rows: AgedStockItem[];
};

export type StockAgeing = {
  buckets: StockAgeBucketRow[];
  total: number;
  valuesWithheld: boolean;
};

/** How stale the stock on the shelf is, by value. Sales only — never journals. */
export function stockAgeing(snapshot: Snapshot, asOf: string): StockAgeing {
  const lastSold = new Map<string, string>();
  for (const v of postedVouchers(snapshot)) {
    if (!isSale(v)) continue;
    for (const line of v.inventoryEntries) {
      const seen = lastSold.get(line.stockItem) ?? "";
      if (v.date > seen) lastSold.set(line.stockItem, v.date);
    }
  }

  const rows: StockAgeBucketRow[] = STOCK_AGE_BUCKETS.map((label) => ({
    label,
    value: 0,
    items: 0,
    rows: [],
  }));
  const held = snapshot.stockItems.filter((i) => i.closingQty > 0.0001);

  for (const item of held) {
    const sold = lastSold.get(item.name);
    const days = sold ? daysBetween(sold, asOf) : Number.POSITIVE_INFINITY;
    const bucket = days <= 30 ? 0 : days <= 60 ? 1 : days <= 90 ? 2 : 3;

    rows[bucket]!.value = money(rows[bucket]!.value + Math.abs(item.closingValue));
    rows[bucket]!.items += 1;
    rows[bucket]!.rows.push({
      item: item.name,
      qty: item.closingQty,
      unit: item.baseUnit,
      value: Math.abs(item.closingValue),
      lastSold: sold ?? null,
      daysSince: sold ? days : null,
    });
  }

  // Biggest value first inside each bucket: opening a bucket is a hunt for what
  // to act on, and the largest sums are what a decision hangs on.
  for (const row of rows) {
    row.rows.sort((a, b) => b.value - a.value || b.qty - a.qty);
  }

  return {
    buckets: rows,
    total: sum(rows.map((r) => r.value)),
    valuesWithheld: held.length > 0 && held.every((i) => i.closingValue === 0),
  };
}

export type Turnover = {
  cogs: number;
  averageStock: number;
  /** times the stock cycled in the window */
  turns: number | null;
  /** the same figure projected to a year, which is how it is usually quoted */
  annualised: number | null;
  windowDays: number;
};

/**
 * Stock turnover.
 *
 * COGS is derived as opening + purchases − closing, because Tally holds no
 * cost-of-sales ledger we can trust across every chart of accounts. It is an
 * approximation and the screen says so.
 */
export function stockTurnover(snapshot: Snapshot): Turnover {
  const opening = Math.abs(sum(snapshot.stockItems.map((i) => i.openingValue)));
  const closing = Math.abs(sum(snapshot.stockItems.map((i) => i.closingValue)));

  const purchases = Math.abs(
    sum(
      postedVouchers(snapshot)
        .filter(isPurchase)
        .flatMap((v) => v.inventoryEntries.map((e) => Math.abs(e.amount))),
    ),
  );

  const dates = postedVouchers(snapshot).map((v) => v.date).sort();
  const windowDays =
    dates.length > 1 ? daysBetween(dates[0]!, dates[dates.length - 1]!) : 0;

  const cogs = money(opening + purchases - closing);
  const averageStock = money((opening + closing) / 2);
  const turns = averageStock > 0 ? cogs / averageStock : null;

  return {
    cogs,
    averageStock,
    turns,
    annualised: turns !== null && windowDays > 0 ? (turns * 365) / windowDays : null,
    windowDays,
  };
}

/* ─────────────────────────────────────────────────────────── 4 · customers ── */

export type RfmCustomer = {
  ledger: string;
  recencyDays: number;
  frequency: number;
  monetary: number;
  r: number;
  f: number;
  m: number;
  segment: RfmSegment;
};

export type RfmSegment = "Champions" | "Loyal" | "At risk" | "Lost" | "New" | "Others";

export type RfmReport = {
  customers: RfmCustomer[];
  /** counts on a 5x5 grid, [r-1][f-1] */
  grid: number[][];
  segments: Array<{ segment: RfmSegment; count: number; value: number }>;
  /** true when there are too few customers for quintiles to mean anything */
  tooFew: boolean;
};

const RFM_MIN_CUSTOMERS = 10;

/**
 * Recency, frequency, monetary — the segmentation the market converged on.
 *
 * Scores are QUINTILES of this company's own customers, not absolute bands.
 * A distributor with 40 invoices a month and a shop with 4 have nothing in
 * common, and any fixed threshold would be wrong for one of them.
 */
export function deriveRfm(
  snapshot: Snapshot,
  asOf: string,
  index: GroupIndex = indexGroups(snapshot),
): RfmReport {
  const { partyOf, valueOf } = salesContext(snapshot, index);

  const raw = new Map<string, { last: string; freq: number; value: number }>();
  for (const v of postedVouchers(snapshot)) {
    if (!isSale(v)) continue;
    const party = partyOf(v);
    if (!party) continue;
    const row = raw.get(party) ?? { last: "", freq: 0, value: 0 };
    if (v.date > row.last) row.last = v.date;
    row.freq += 1;
    row.value = money(row.value + valueOf(v));
    raw.set(party, row);
  }

  const list = [...raw.entries()].map(([ledger, r]) => ({
    ledger,
    recencyDays: daysBetween(r.last, asOf),
    frequency: r.freq,
    monetary: r.value,
  }));

  if (list.length < RFM_MIN_CUSTOMERS) {
    return { customers: [], grid: emptyGrid(), segments: [], tooFew: true };
  }

  // Recency is scored INVERTED: fewer days since the last order is better.
  const score = (values: number[], value: number, higherIsBetter: boolean): number => {
    const sorted = [...values].sort((a, b) => a - b);
    const rank = sorted.filter((v) => v < value).length / sorted.length;
    const band = Math.min(5, Math.floor(rank * 5) + 1);
    return higherIsBetter ? band : 6 - band;
  };

  const recencies = list.map((c) => c.recencyDays);
  const freqs = list.map((c) => c.frequency);
  const values = list.map((c) => c.monetary);

  const customers: RfmCustomer[] = list.map((c) => {
    const r = score(recencies, c.recencyDays, false);
    const f = score(freqs, c.frequency, true);
    const m = score(values, c.monetary, true);
    return { ...c, r, f, m, segment: segmentFor(r, f) };
  });

  const grid = emptyGrid();
  for (const c of customers) grid[c.r - 1]![c.f - 1]! += 1;

  const bySegment = new Map<RfmSegment, { count: number; value: number }>();
  for (const c of customers) {
    const row = bySegment.get(c.segment) ?? { count: 0, value: 0 };
    row.count += 1;
    row.value = money(row.value + c.monetary);
    bySegment.set(c.segment, row);
  }

  return {
    customers: customers.sort((a, b) => b.monetary - a.monetary),
    grid,
    segments: [...bySegment.entries()]
      .map(([segment, row]) => ({ segment, ...row }))
      .sort((a, b) => b.value - a.value),
    tooFew: false,
  };
}

function emptyGrid(): number[][] {
  return Array.from({ length: 5 }, () => new Array(5).fill(0) as number[]);
}

function segmentFor(r: number, f: number): RfmSegment {
  if (r >= 4 && f >= 4) return "Champions";
  if (f >= 4) return "Loyal";
  if (r <= 2 && f >= 3) return "At risk";
  if (r <= 2) return "Lost";
  if (r >= 4 && f <= 2) return "New";
  return "Others";
}

export type ConcentrationPoint = { customerPercent: number; revenuePercent: number };

/**
 * The Lorenz curve of customer concentration.
 *
 * The diagonal is perfect evenness; the further the curve bows away from it,
 * the more the business depends on a handful of relationships.
 */
export function customerConcentration(
  snapshot: Snapshot,
  index: GroupIndex = indexGroups(snapshot),
): { points: ConcentrationPoint[]; topFiveShare: number; customers: number } {
  const { partyOf, valueOf } = salesContext(snapshot, index);

  const totals = new Map<string, number>();
  for (const v of postedVouchers(snapshot)) {
    if (!isSale(v)) continue;
    const party = partyOf(v);
    if (!party) continue;
    totals.set(party, money((totals.get(party) ?? 0) + valueOf(v)));
  }

  const sorted = [...totals.values()].sort((a, b) => b - a);
  const total = sum(sorted);
  if (total <= 0 || sorted.length === 0) {
    return { points: [{ customerPercent: 0, revenuePercent: 0 }], topFiveShare: 0, customers: 0 };
  }

  const points: ConcentrationPoint[] = [{ customerPercent: 0, revenuePercent: 0 }];
  let running = 0;
  sorted.forEach((value, i) => {
    running += value;
    points.push({
      customerPercent: ((i + 1) / sorted.length) * 100,
      revenuePercent: (running / total) * 100,
    });
  });

  return {
    points,
    topFiveShare: (sum(sorted.slice(0, 5)) / total) * 100,
    customers: sorted.length,
  };
}

/* ─────────────────────────────────────────────────────────── 5 · financial ── */

export type WaterfallStep = { label: string; value: number; kind: "start" | "add" | "subtract" | "total" };

/** Income, less expenses, equals profit — as steps rather than three numbers. */
export function profitWaterfall(income: number, expenses: number, netProfit: number): WaterfallStep[] {
  return [
    { label: "Income", value: income, kind: "start" },
    { label: "Expenses", value: -Math.abs(expenses), kind: "subtract" },
    { label: "Net profit", value: netProfit, kind: "total" },
  ];
}

/* ────────────────────────────────────────────────────────────────── util ── */

function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}
